// Sección "Plantillas" embebida en la home de Interfaz V2 — SOLO desktop
// (el parent la envuelve en `hidden sm:block`; en mobile las colecciones
// se muestran como rows en la home, igual que siempre).
//
// Es la misma UI que la página `/interfaz-v2/plantillas` (chips de
// categorías single-select + grilla masonry), pero embebida: no fetchea
// nada (recibe las colecciones que la home ya cargó) y al tocar una
// plantilla delega en el handler de la home, que abre el sheet in-state
// sin navegar (no se pierde el scroll de la home).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { CollectionV2, PresetV2 } from '../mockData';
import {
  getAppScrollYFor,
  setAppScrollY,
} from '../../../utils/appScroll';
import { ASPECT_CYCLE, MasonryCard } from './TemplateMasonry';

interface FlatPreset extends PresetV2 {
  /** id de la colección de origen — para pasarle al handler la colección
   *  correcta (sus presets alimentan el swipe del sheet). */
  collectionId: string;
}

interface HomeTemplatesSectionProps {
  /** Colecciones ya cargadas por la home (sin la fila virtual de modos). */
  collections: CollectionV2[];
  /** Abre el preset — la home conecta acá su `presetClick` existente. */
  onPresetClick: (preset: PresetV2, collection: CollectionV2) => void;
  /** Ancho del sidebar — el chip-bar fijo arranca a su derecha. */
  sidebarWidth: number;
  /** Notifica cuando el chip-bar pasa a fixed, para que la home oculte
   *  el chrome flotante que colisiona (botón "Contactanos"). */
  onChipsStuckChange?: (stuck: boolean) => void;
}

export const HomeTemplatesSection: React.FC<HomeTemplatesSectionProps> = ({
  collections,
  onPresetClick,
  sidebarWidth,
  onChipsStuckChange,
}) => {
  // Single-select: 'all' = mostrar todas las plantillas (default). Solo
  // en memoria — el sheet se abre in-state (la sección no se desmonta),
  // así que el filtro sobrevive sin sessionStorage.
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('all');

  // Mismo orden que la página de Plantillas: "Alfombra roja" al final.
  const categories = useMemo(() => {
    const real = collections.filter((c) => c.id !== 'design-row');
    const isAlfombraRoja = (title?: string) =>
      (title || '').trim().toLowerCase() === 'alfombra roja';
    return [
      ...real.filter((c) => !isAlfombraRoja(c.title)),
      ...real.filter((c) => isAlfombraRoja(c.title)),
    ];
  }, [collections]);

  // Flat + dedupe por preset.id (si un preset está en varias categorías,
  // gana la primera que lo contiene) — igual que la página.
  const allPresets = useMemo<FlatPreset[]>(() => {
    const seen = new Set<string>();
    const out: FlatPreset[] = [];
    for (const c of categories) {
      for (const p of c.presets) {
        if (seen.has(p.id)) continue;
        seen.add(p.id);
        out.push({ ...p, collectionId: c.id });
      }
    }
    return out;
  }, [categories]);

  const filteredPresets = useMemo(() => {
    if (selectedCategoryId === 'all') return allPresets;
    return allPresets.filter((p) => p.collectionId === selectedCategoryId);
  }, [allPresets, selectedCategoryId]);

  // Sticky fake-out — mismo patrón que InterfazV2Plantillas: `position:
  // sticky` no funciona acá (el `<main>` de DashboardLayout tiene
  // `overflow-x-hidden` y se vuelve el scroll container del sticky, pero
  // el que scrollea de verdad es el body → sticky congelado). Workaround:
  // sentinel + IntersectionObserver; cuando sale por arriba, el chip-bar
  // pasa a `position: fixed` con un spacer del mismo alto en el flow.
  const sectionRef = useRef<HTMLElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const chipsBarRef = useRef<HTMLDivElement | null>(null);
  const [isStuck, setIsStuck] = useState(false);
  const [chipsHeight, setChipsHeight] = useState(0);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsStuck(
          !entry.isIntersecting && entry.boundingClientRect.top < 0,
        );
      },
      { threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [categories]);
  useEffect(() => {
    if (!chipsBarRef.current) return;
    setChipsHeight(chipsBarRef.current.offsetHeight);
  }, [categories, isStuck]);
  // Notificamos vía ref para no depender de la identidad del callback:
  // si el parent pasara una arrow inline, estos effects re-dispararían
  // en cada render suyo (y el cleanup de desmontaje correría en cada
  // cambio de identidad, no solo al desmontar).
  const onChipsStuckChangeRef = useRef(onChipsStuckChange);
  useEffect(() => {
    onChipsStuckChangeRef.current = onChipsStuckChange;
  });
  useEffect(() => {
    onChipsStuckChangeRef.current?.(isStuck);
  }, [isStuck]);
  // Al desmontar, liberamos el chrome que la home haya ocultado.
  useEffect(
    () => () => {
      onChipsStuckChangeRef.current?.(false);
    },
    [],
  );

  // Al cambiar de categoría con el bar fijo (user scrolleado dentro de la
  // grilla), volvemos suave al tope de la SECCIÓN — no de la página, que
  // arriba tiene los modos. Si el bar está en su posición natural no hay
  // nada que scrollear. Siempre vía utils/appScroll: en desktop el
  // scroller es el panel `.content-panel-clip`, no el window.
  const selectCategory = (id: string) => {
    if (id === selectedCategoryId) return;
    setSelectedCategoryId(id);
    if (!isStuck) return;
    const el = sectionRef.current;
    if (!el) return;
    setAppScrollY(Math.max(0, getAppScrollYFor(el) - 12), 'smooth');
  };

  // ── Flechas de scroll del row de categorías — igual que la página. ──
  const categoryScrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const measureCategoryArrows = useCallback(() => {
    const el = categoryScrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);
  useEffect(() => {
    measureCategoryArrows();
    const raf = requestAnimationFrame(measureCategoryArrows);
    window.addEventListener('resize', measureCategoryArrows);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measureCategoryArrows);
    };
  }, [measureCategoryArrows, categories, isStuck]);
  const scrollCategories = (dir: -1 | 1) => {
    const el = categoryScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.75, behavior: 'smooth' });
  };

  const openPreset = (p: FlatPreset) => {
    const sourceCollection = collections.find((c) => c.id === p.collectionId);
    if (!sourceCollection) return;
    onPresetClick(p, sourceCollection);
  };

  if (allPresets.length === 0) return null;

  return (
    <section ref={sectionRef}>
      {/* Título con el estilo de sección de la home (h2 de CollectionRow),
          no el h1 grande de la página standalone. */}
      <header className="mb-3">
        <h2 className="text-[18px] sm:text-[20px] font-semibold text-brand-black/90 leading-tight">
          Plantillas
        </h2>
      </header>

      {/* Row de chips — mismo markup que la página; solo la variante
          desktop (fondo surface-content) porque la sección es sm+. */}
      <div ref={sentinelRef} aria-hidden="true" />
      <div
        ref={chipsBarRef}
        className={
          isStuck
            ? 'fixed top-0 right-0 z-30 pb-3 sm:pb-4'
            : 'bg-surface-content border-b border-black/[0.06] -mx-3 sm:-mx-5 relative mb-3 sm:mb-4'
        }
        style={
          isStuck
            ? {
                left: sidebarWidth,
                background:
                  'linear-gradient(to bottom, #FBFBFD 0%, #FBFBFD 70%, rgba(251,251,253,0) 100%)',
              }
            : undefined
        }
      >
        <div className={`relative ${isStuck ? 'max-w-content mx-auto' : ''}`}>
          {/* Flechas: mismo patrón wrapper-flex que la página (el wrapper
              posiciona/centra para que el `:active { scale }` global no
              pise un translate del botón). */}
          <div className="hidden sm:flex absolute left-1 inset-y-0 z-10 items-center pointer-events-none">
            <button
              type="button"
              aria-label="Ver categorías anteriores"
              onClick={() => scrollCategories(-1)}
              className={`w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-md ring-1 ring-black/[0.06] text-brand-black/70 hover:text-brand-black transition-opacity duration-200 ${
                canScrollLeft
                  ? 'opacity-100 pointer-events-auto'
                  : 'opacity-0 pointer-events-none'
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          </div>
          <div
            ref={categoryScrollRef}
            onScroll={measureCategoryArrows}
            className={`flex gap-2 overflow-x-auto scrollbar-hide py-3 sm:py-4 ${
              isStuck ? 'px-5 sm:px-8' : 'px-3 sm:px-5'
            }`}
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {[{ id: 'all', title: 'Todas las plantillas' }, ...categories].map(
              (c, i) => {
                const active = selectedCategoryId === c.id;
                const isFirstCategory = i === 1;
                return (
                  <React.Fragment key={c.id}>
                    {isFirstCategory && (
                      <span
                        aria-hidden="true"
                        className="shrink-0 self-center w-px h-5 bg-black/10 mx-1"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => selectCategory(c.id)}
                      className={`shrink-0 px-4 py-2 rounded-full text-[14px] font-medium transition-colors border ${
                        active
                          ? 'border-brand-primary text-brand-primary bg-brand-primary/[0.04]'
                          : 'border-brand-black/80 text-brand-black/90 bg-white hover:bg-brand-black/[0.03]'
                      }`}
                    >
                      {c.title}
                    </button>
                  </React.Fragment>
                );
              },
            )}
          </div>
          <div className="hidden sm:flex absolute right-1 inset-y-0 z-10 items-center pointer-events-none">
            <button
              type="button"
              aria-label="Ver más categorías"
              onClick={() => scrollCategories(1)}
              className={`w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-md ring-1 ring-black/[0.06] text-brand-black/70 hover:text-brand-black transition-opacity duration-200 ${
                canScrollRight
                  ? 'opacity-100 pointer-events-auto'
                  : 'opacity-0 pointer-events-none'
              }`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
      {/* Spacer cuando el bar está fijo — evita el salto del grid. */}
      {isStuck && (
        <div aria-hidden="true" style={{ height: chipsHeight, marginBottom: 12 }} />
      )}

      {/* Grilla masonry — idéntica a la página. */}
      {filteredPresets.length === 0 ? (
        <div className="text-[14px] text-[#3C3C43]/60">
          No hay plantillas para los filtros seleccionados.
        </div>
      ) : (
        <div
          key={selectedCategoryId}
          className="columns-2 sm:columns-3 lg:columns-4 gap-3 sm:gap-4 animate-category-swap"
        >
          {filteredPresets.map((p, i) => (
            <MasonryCard
              key={p.id}
              preset={p}
              aspectClass={ASPECT_CYCLE[i % ASPECT_CYCLE.length]}
              onClick={() => openPreset(p)}
            />
          ))}
        </div>
      )}
    </section>
  );
};
